import { useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { isFcmClientEnabled } from "../../config/firebase";
import { canRegisterFcmForUser } from "../../helpers/fcm-role";
import {
  enableFcmNotificationsFromUserGesture,
  getFcmNotificationPermission,
} from "../../services/fcm.service";

/**
 * Non-intrusive prompt shown only when notification permission is still "default".
 * Permission is requested exclusively from the Enable button click (user gesture).
 */
export default function FcmEnableNotificationsPrompt({ onEnabled }) {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const authInfo = useSelector((state) => state.auth.authInfo);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  if (
    !isFcmClientEnabled() ||
    hidden ||
    !isLoggedIn ||
    !authInfo?.token ||
    !canRegisterFcmForUser(userInfo) ||
    getFcmNotificationPermission() !== "default"
  ) {
    return null;
  }

  const handleEnableClick = async () => {
    setBusy(true);
    try {
      const result = await enableFcmNotificationsFromUserGesture(authInfo, userInfo);
      if (result?.success) {
        onEnabled?.();
        setHidden(true);
      } else if (result?.permission === "denied") {
        setHidden(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <style>{`
        .fcm-enable-prompt {
          bottom: 16px;
          z-index: 1040;
          width: min(92%, 420px);
          max-width: 420px;
        }
        @media (min-width: 992px) {
          .fcm-enable-prompt {
            bottom: 20px;
          }
        }
      `}</style>
      <div
        className="position-fixed start-50 translate-middle-x fcm-enable-prompt"
        role="region"
        aria-label="Enable notifications"
      >
        <div className="card border-0 shadow-sm mb-0 bg-white" style={{ borderRadius: "12px" }}>
          <div className="card-body d-flex align-items-center justify-content-between gap-2 py-2 px-3">
            <div className="small text-muted mb-0 pe-2">
              Get browser alerts for messages and milestones.
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              className="flex-shrink-0"
              disabled={busy}
              onClick={handleEnableClick}
            >
              {busy ? "Enabling…" : "Enable notifications"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
